import React from 'react'
import { Decision } from '_gql'
import { useMutation } from '@apollo/client'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { TOAST_TYPE_DANGER, useToast } from '@divvy-web/skylab.toast'

import { StatusButtons } from '@sputnik/components'
import { PersonaClient } from '@sputnik/persona'
import { logError } from '@sputnik/utils/loggerUtils'

import { useGetPersonaStatusesLazyQuery } from './GetPersonaStatuses.gql'
import RestartPersonaInquiry from './RestartPersonaInquiry.gql'

interface PersonVerificationButtonsProps {
  inquiry: {
    inquiryId: string
    person: {
      firstName: string
      lastName: string
    }
    status: string
  }
  showGoToOfferButton: boolean
  handleGoToOffers: () => void
  decisions: Decision[]
  salesforceCreditId: string
  refetch: () => void
}

const PersonVerificationButtons = ({
  inquiry,
  refetch,
  showGoToOfferButton,
  handleGoToOffers,
  decisions,
  salesforceCreditId,
}: PersonVerificationButtonsProps) => {
  const [restartPersonaInquiry, { loading: isResartingPersonaInquiry }] = useMutation(RestartPersonaInquiry)
  const [makeTestId] = useNameFormatter('PersonVerificationButtons')
  const showDangerToast = useToast(TOAST_TYPE_DANGER) as any
  const [fetchPersonaInquiries, { loading }] = useGetPersonaStatusesLazyQuery({
    fetchPolicy: 'network-only',
  })

  const isInquiryStarted = inquiry?.status !== 'CREATED'

  const handleVerifyPerson = async () => {
    try {
      const currentInquiry = isInquiryStarted
        ? await restartPersonaInquiry({ variables: { inquiryId: inquiry.inquiryId } })
            .then((result) => {
              fetchPersonaInquiries()
              return result?.data?.restartPersonaInquiry ?? {}
            })
            .catch((error) => {
              showDangerToast(
                <FormattedMessage
                  defaultMessage='Could not start verification due to an error. Please try again later.'
                  id='sputnik.PersonVerificationButtons__7C7iKR'
                />,
                {
                  autoHideDelay: 5000,
                },
              )
              logError(error, {
                attributes: {
                  action: 'restartPersonaInquiry',
                  creditAppId: salesforceCreditId,
                  message: error?.message,
                  result: 'Error restarting persona inquiry',
                },
                eventName: 'RestartPersonaInquiry',
              })
              return {}
            })
        : inquiry

      const personaClient = new PersonaClient(currentInquiry.inquiryId, refetch)
      await personaClient.open()
    } catch (error) {
      showDangerToast(
        <FormattedMessage
          defaultMessage='Could not start verification due to an error. Please try again later.'
          id='sputnik.PersonVerificationButtons__7C7iKR'
        />,
        {
          autoHideDelay: 5000,
        },
      )
      logError(error, {
        attributes: {
          action: 'personaClientOpen',
          creditAppId: salesforceCreditId,
          message: error,
          result: 'Error restarting persona inquiry',
        },
        eventName: 'HandleVerifyPerson',
      })
    }
  }

  return (
    <StatusButtons
      dataTestId={makeTestId(`${inquiry.inquiryId}`)}
      hideSecondary={!showGoToOfferButton}
      primaryLoading={isResartingPersonaInquiry || loading}
      primaryText={
        <FormattedMessage
          defaultMessage='Verify identity'
          id='sputnik.PersonVerificationButtons__F/MMgU'
        />
      }
      secondaryText={
        <FormattedMessage
          defaultMessage='Go to {offers, plural, one {offer} other {offers}}'
          id='sputnik.PersonVerificationButtons__FBxOac'
          values={{ offers: decisions?.length }}
        />
      }
      onPrimaryClick={handleVerifyPerson}
      onSecondaryClick={handleGoToOffers}
    />
  )
}

export default PersonVerificationButtons
